(function () {
  if (window.MagazineArchive) {
    window.MagazineArchive.init(document);
    return;
  }

  var cache = new Map();
  var controllers = new WeakMap();
  var activeControllers = new Set();
  var sourceParameters = ['category', 'sort', 'archive_page', 'q', 'page', 'section_id', 'sections'];
  var maxQueryLength = 100;
  var defaults = {
    all: 'All Articles',
    loading: 'Loading articles...',
    error: 'We could not load the articles. Please try again.',
    empty: 'No articles found in this category.',
    showing: 'Showing [start]-[end] of [total] articles',
    previous: 'Previous',
    next: 'Next',
    page_label: 'Page [page]',
    pagination: 'Article pages',
    search_empty: 'No released articles match “[term]”.',
    search_empty_in: 'No released articles in [topic] match “[query]”.',
  };

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function cleanQuery(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxQueryLength)
      .replace(/^["'\u2018\u2019\u201c\u201d\s]+|["'\u2018\u2019\u201c\u201d\s]+$/g, '');
  }

  function fold(value, joinWords) {
    var text = String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2018\u2019\u201a\u201b\u2032]/g, "'")
      .replace(/[\u201c\u201d\u201e\u201f\u2033]/g, '"');
    // Joined form: "ER-100" also reads "er100" and "GLP-1" reads "glp1"; spaced words stay apart.
    if (joinWords) text = text.replace(/([a-z0-9])[-.'\/_](?=[a-z0-9])/g, '$1');
    return text.replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function queryTokens(query) {
    var folded = fold(query);
    return folded ? folded.split(' ') : [];
  }

  // Tokens match at word starts, so "rapa" finds rapamycin but "ai" does not match inside "brain".
  function matches(record, tokens) {
    return (
      tokens.length > 0 &&
      tokens.every(function (token) {
        return record.search.indexOf(' ' + token) !== -1;
      })
    );
  }

  function fill(template, values) {
    return String(template).replace(/\[(term|query|topic)\]/g, function (placeholder, key) {
      return values[key === 'query' ? 'term' : key] || '';
    });
  }

  function positiveInteger(value, fallback) {
    var number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : fallback;
  }

  function sourceURL(value) {
    if (!value) throw new Error('The magazine archive URL is missing.');
    var url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) throw new Error('Invalid magazine archive origin.');
    new URL(window.location.href).searchParams.forEach(function (parameter, key) {
      if (key !== 'view' && sourceParameters.indexOf(key) === -1 && !url.searchParams.has(key)) {
        url.searchParams.set(key, parameter);
      }
    });
    sourceParameters.forEach(function (key) {
      url.searchParams.delete(key);
    });
    url.searchParams.sort();
    url.hash = '';
    return url;
  }

  function readSource(root) {
    var source = root && root.querySelector('[data-archive-source]');
    if (!source) throw new Error('The magazine archive source is missing.');
    var page = positiveInteger(source.dataset.page, 0);
    var pages = source.dataset.pages === '0' ? 1 : positiveInteger(source.dataset.pages, 0);
    if (!page || !pages || page > pages) throw new Error('Invalid magazine archive pagination.');
    var records = Array.from(source.children)
      .filter(function (element) {
        return element.matches('template[data-archive-card]');
      })
      .map(function (template) {
        var id = template.dataset.id || '';
        if (!/^\d+$/.test(id) || !template.content.firstElementChild) {
          throw new Error('Invalid magazine archive article.');
        }
        var categories = [];
        var categoryKeys = [];
        (template.dataset.categories || '').split('||').forEach(function (value) {
          var label = value.trim();
          var key = normalize(label);
          if (key && categoryKeys.indexOf(key) === -1) {
            if (label === label.toUpperCase()) {
              label = label.toLowerCase().replace(/(^|\s)\S/g, function (letter) {
                return letter.toUpperCase();
              });
            }
            categories.push(label);
            categoryKeys.push(key);
          }
        });
        var published = Number(template.dataset.published);
        if (!Number.isFinite(published) || published < 0) published = 0;
        var date = Number(template.dataset.date);
        if (!Number.isFinite(date) || date <= 0) date = published;
        var title = template.dataset.title || '';
        var authors = template.dataset.authors || '';
        var teaser = template.content.querySelector('.mag-aplace__row-teaser');
        var text = [title, authors, categories.join(' '), teaser ? teaser.textContent : ''].join(' ');
        return {
          id: id,
          title: title,
          authors: authors,
          date: date,
          published: published,
          categories: categories,
          categoryKeys: categoryKeys,
          search: ' ' + fold(text) + ' ' + fold(text, true) + ' ',
          template: template,
        };
      });
    return { root: root, page: page, pages: pages, records: records };
  }

  async function fetchSource(url) {
    var abort = new AbortController();
    var timeout = window.setTimeout(function () {
      abort.abort();
    }, 20000);
    try {
      var response = await fetch(url.href, { credentials: 'same-origin', signal: abort.signal });
      if (!response.ok) throw new Error('The magazine archive request failed.');
      var html = await response.text();
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      return readSource(parsed.querySelector('[data-magazine-archive]'));
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function collect(url, options) {
    var first;
    var sectionId = options.sectionId;
    if (options.root) {
      first = readSource(options.root);
    } else {
      var firstURL = new URL(url.href);
      firstURL.searchParams.set('page', '1');
      if (sectionId) firstURL.searchParams.set('section_id', sectionId);
      first = await fetchSource(firstURL);
      if (first.page !== 1) throw new Error('The magazine archive returned the wrong page.');
    }
    // The full alternate template supplies its actual section ID and editor settings.
    sectionId = first.root.dataset.sectionId || sectionId;
    if (first.pages > 1 && !sectionId) throw new Error('The magazine archive section is missing.');
    var resolvedURL = sourceURL(first.root.dataset.sourceUrl || url.href);
    url.searchParams.forEach(function (value, key) {
      if (!resolvedURL.searchParams.has(key)) resolvedURL.searchParams.set(key, value);
    });
    url = resolvedURL;

    var pages = new Map([[first.page, first.records]]);
    var pending = [];
    for (var page = 1; page <= first.pages; page += 1) {
      if (page !== first.page) pending.push(page);
    }
    var cursor = 0;
    var failure;
    async function worker() {
      while (!failure && cursor < pending.length) {
        var next = pending[cursor++];
        var nextURL = new URL(url.href);
        nextURL.searchParams.set('section_id', sectionId);
        nextURL.searchParams.set('page', String(next));
        try {
          var result = await fetchSource(nextURL);
          if (result.page !== next || result.pages !== first.pages) {
            throw new Error('The magazine archive changed while loading.');
          }
          pages.set(next, result.records);
        } catch (error) {
          failure = error;
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, pending.length) }, worker));
    if (failure) throw failure;

    var records = [];
    var ids = new Set();
    for (var number = 1; number <= first.pages; number += 1) {
      pages.get(number).forEach(function (record) {
        if (ids.has(record.id)) throw new Error('The magazine archive changed while loading.');
        ids.add(record.id);
        records.push(record);
      });
    }
    return records;
  }

  function load(options) {
    options = options || {};
    var url;
    try {
      url = sourceURL(options.url);
    } catch (error) {
      return Promise.reject(error);
    }
    var key = url.href;
    if (cache.has(key)) return cache.get(key);
    var promise = collect(url, options).catch(function (error) {
      if (cache.get(key) === promise) cache.delete(key);
      throw error;
    });
    cache.set(key, promise);
    return promise;
  }

  function readState() {
    var parameters = new URL(window.location.href).searchParams;
    return {
      category: normalize(parameters.get('category')),
      label: (parameters.get('category') || '').trim(),
      sort: parameters.get('sort') === 'oldest' ? 'oldest' : 'latest',
      page: positiveInteger(parameters.get('archive_page'), 1),
      query: cleanQuery(parameters.get('q')),
    };
  }

  function stateURL(state) {
    var url = new URL(window.location.href);
    ['page', 'section_id', 'sections'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    if (state.category) url.searchParams.set('category', state.category);
    else url.searchParams.delete('category');
    if (state.sort === 'oldest') url.searchParams.set('sort', 'oldest');
    else url.searchParams.delete('sort');
    if (state.page > 1) url.searchParams.set('archive_page', String(state.page));
    else url.searchParams.delete('archive_page');
    if (state.query) url.searchParams.set('q', state.query);
    else url.searchParams.delete('q');
    return url;
  }

  function writeState(state, push) {
    var url = stateURL(state);
    if (url.href !== window.location.href) {
      window.history[push ? 'pushState' : 'replaceState'](window.history.state, '', url.href);
    }
  }

  function compareArticles(left, right) {
    var difference = left.date - right.date || left.published - right.published;
    if (difference) return difference;
    // Article IDs are decimal strings, so ties remain stable without numeric rounding.
    return left.id.length - right.id.length || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0);
  }

  function Archive(root) {
    this.root = root;
    this.url = root.dataset.sourceUrl;
    this.pageSize = positiveInteger(root.dataset.pageSize, 10);
    this.state = readState();
    this.records = null;
    this.labels = new Map();
    this.elements = {};
    this.i18n = Object.assign({}, defaults);
    try {
      Object.assign(this.i18n, JSON.parse(root.dataset.archiveI18n || '{}'));
    } catch (error) {
      // Defaults keep the controls usable if an editor-provided translation is invalid.
    }
    this.i18n.all = root.dataset.allLabel || this.i18n.all;
    var self = this;
    [
      'category',
      'sort',
      'heading',
      'search',
      'search-form',
      'search-input',
      'search-clear',
      'status',
      'results',
      'pagination',
      'empty',
      'empty-text',
      'empty-actions',
      'empty-clear',
      'browse',
      'error',
      'retry',
      'controls',
    ].forEach(function (name) {
      self.elements[name] = root.querySelector('[data-archive-' + name + ']');
    });
    this.onChange = function (event) {
      if (event.target !== self.elements.category && event.target !== self.elements.sort) return;
      if (!self.records) return;
      self.state.category = normalize(self.elements.category.value);
      self.state.label = self.elements.category.selectedOptions[0].textContent;
      self.state.sort = self.elements.sort.value === 'oldest' ? 'oldest' : 'latest';
      self.state.page = 1;
      self.render(true);
    };
    this.onClick = function (event) {
      var button = event.target.closest(
        [
          'a[data-archive-page]',
          'a[data-archive-browse]',
          '[data-archive-retry]',
          '[data-archive-search-clear]',
          '[data-archive-empty-clear]',
        ].join(', '),
      );
      if (!button || !root.contains(button)) return;
      if (button === self.elements.retry) {
        event.preventDefault();
        self.start(false);
        return;
      }
      if (button === self.elements['search-clear'] || button === self.elements['empty-clear']) {
        event.preventDefault();
        self.clearSearch();
        return;
      }
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (button.target && button.target !== '_self') ||
        button.hasAttribute('download') ||
        !self.records ||
        self.loading
      )
        return;
      if (button === self.elements.browse) {
        event.preventDefault();
        self.state.query = '';
        self.state.category = '';
        self.state.label = '';
        self.state.page = 1;
        self.render(true, true);
        return;
      }
      var page = positiveInteger(button.dataset.archivePage, 0);
      if (!page) return;
      event.preventDefault();
      self.state.page = page;
      self.render(true, true);
    };
    this.onSubmit = function (event) {
      if (event.target !== self.elements['search-form'] || !self.records) return;
      event.preventDefault();
      var query = cleanQuery(self.elements['search-input'].value);
      if (!query) {
        self.clearSearch();
        return;
      }
      // Refining from the results field keeps the topic, the sort and the focus in the field.
      self.state.query = query;
      self.state.page = 1;
      self.render(true);
    };
    this.onInput = function (event) {
      if (event.target === self.elements['search-input']) self.syncSearchFilled();
    };
    root.addEventListener('change', this.onChange);
    root.addEventListener('click', this.onClick);
    root.addEventListener('submit', this.onSubmit);
    root.addEventListener('input', this.onInput);
    this.start(true);
  }

  Archive.prototype.start = async function (initial) {
    if (this.loading) return;
    this.loading = true;
    this.root.setAttribute('aria-busy', 'true');
    if (this.elements.status) {
      this.elements.status.classList.remove('visually-hidden');
      this.elements.status.textContent = this.i18n.loading;
    }
    if (this.elements.error) this.elements.error.hidden = true;
    if (this.elements.empty) this.elements.empty.hidden = true;
    if (this.elements.pagination) this.elements.pagination.hidden = true;
    if (this.elements.retry) this.elements.retry.disabled = true;
    try {
      this.records = await load({ url: this.url, root: initial ? this.root : null });
      if (this.destroyed || !this.root.isConnected) return;
      var self = this;
      this.records.forEach(function (record) {
        record.categoryKeys.forEach(function (key, index) {
          if (!self.labels.has(key)) self.labels.set(key, record.categories[index]);
        });
      });
      if (this.elements.controls) this.elements.controls.hidden = false;
      this.render(false);
    } catch (error) {
      if (this.destroyed || !this.root.isConnected) return;
      if (this.elements.error) this.elements.error.hidden = false;
      if (this.elements.status) {
        this.elements.status.classList.add('visually-hidden');
        this.elements.status.textContent = this.i18n.error;
      }
    } finally {
      this.loading = false;
      this.root.setAttribute('aria-busy', 'false');
      if (this.elements.retry) this.elements.retry.disabled = false;
    }
  };

  Archive.prototype.updateOptions = function () {
    var select = this.elements.category;
    if (!select) return;
    var self = this;
    var options = [['', this.i18n.all]].concat(
      Array.from(this.labels).sort(function (left, right) {
        return left[1].localeCompare(right[1], document.documentElement.lang || 'en', { sensitivity: 'base' });
      }),
    );
    if (this.state.category && !this.labels.has(this.state.category)) {
      options.push([this.state.category, this.state.label || this.state.category]);
    }
    select.replaceChildren();
    options.forEach(function (entry) {
      var option = document.createElement('option');
      option.value = entry[0];
      option.textContent = entry[1];
      option.selected = entry[0] === self.state.category;
      select.appendChild(option);
    });
  };

  Archive.prototype.renderPagination = function (totalPages) {
    var navigation = this.elements.pagination;
    if (!navigation) return;
    navigation.replaceChildren();
    navigation.hidden = totalPages <= 1;
    if (totalPages <= 1) return;
    if (!navigation.hasAttribute('aria-label')) navigation.setAttribute('aria-label', this.i18n.pagination);
    var self = this;
    var iconTemplate = this.root.querySelector('[data-archive-page-icon]');
    var windowStart = Math.max(1, Math.min(this.state.page - 1, totalPages - 2));
    var windowEnd = Math.min(totalPages, windowStart + 2);
    function link(page, label, relation) {
      var anchor = document.createElement('a');
      anchor.className = 'magazine-archive__page';
      anchor.href = stateURL(Object.assign({}, self.state, { page: page })).href;
      anchor.dataset.archivePage = String(page);
      anchor.textContent = label;
      if (relation) {
        anchor.classList.add('magazine-archive__page--' + relation);
        anchor.rel = relation === 'previous' ? 'prev' : 'next';
        anchor.setAttribute('aria-label', label);
        anchor.title = label;
        if (iconTemplate && iconTemplate.content) {
          anchor.replaceChildren(iconTemplate.content.cloneNode(true));
        }
      } else {
        if (page < windowStart || page > windowEnd) {
          anchor.classList.add('magazine-archive__page--outside-window');
        }
        anchor.setAttribute('aria-label', self.i18n.page_label.replace('[page]', String(page)));
        if (page === self.state.page) anchor.setAttribute('aria-current', 'page');
      }
      navigation.appendChild(anchor);
    }
    if (this.state.page > 1) link(this.state.page - 1, this.i18n.previous, 'previous');
    var previous = 0;
    for (var page = 1; page <= totalPages; page += 1) {
      if (totalPages > 5 && page !== 1 && page !== totalPages && (page < windowStart || page > windowEnd)) continue;
      if (previous && page > previous + 1) {
        var ellipsis = document.createElement('span');
        ellipsis.className = 'magazine-archive__ellipsis';
        ellipsis.setAttribute('aria-hidden', 'true');
        ellipsis.textContent = '...';
        navigation.appendChild(ellipsis);
      }
      link(page, String(page));
      previous = page;
    }
    if (this.state.page < totalPages) link(this.state.page + 1, this.i18n.next, 'next');
  };

  Archive.prototype.render = function (push, focusHeading) {
    if (!this.records || !this.elements.results) return;
    var state = this.state;
    var tokens = queryTokens(state.query);
    var filtered = this.records.filter(function (record) {
      if (state.category && record.categoryKeys.indexOf(state.category) === -1) return false;
      return !state.query || matches(record, tokens);
    });
    filtered.sort(compareArticles);
    if (state.sort === 'latest') filtered.reverse();
    var totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
    state.page = Math.min(state.page, totalPages);
    var offset = (state.page - 1) * this.pageSize;
    var visible = filtered.slice(offset, offset + this.pageSize);
    var fragment = document.createDocumentFragment();
    visible.forEach(function (record) {
      var card = record.template.content.cloneNode(true);
      card.querySelectorAll('img').forEach(function (image) {
        image.loading = 'lazy';
      });
      fragment.appendChild(card);
    });
    this.elements.results.replaceChildren(fragment);
    this.updateOptions();
    if (this.elements.sort) this.elements.sort.value = state.sort;
    var topic = state.category ? this.labels.get(state.category) || state.label || state.category : '';
    var emptyText = this.i18n.empty;
    if (state.query) {
      emptyText = fill(topic ? this.i18n.search_empty_in : this.i18n.search_empty, { term: state.query, topic: topic });
    }
    if (this.elements.heading) this.elements.heading.textContent = topic || this.i18n.all;
    if (this.elements.search) this.elements.search.hidden = !state.query;
    if (this.elements['search-input']) {
      this.elements['search-input'].value = state.query;
      this.syncSearchFilled();
    }
    if (this.elements.status) {
      this.elements.status.classList.toggle('visually-hidden', filtered.length === 0);
      this.elements.status.textContent = filtered.length
        ? this.i18n.showing
            .replace('[start]', String(offset + 1))
            .replace('[end]', String(offset + visible.length))
            .replace('[total]', String(filtered.length))
        : emptyText;
    }
    if (this.elements.empty) {
      (this.elements['empty-text'] || this.elements.empty).textContent = emptyText;
      this.elements.empty.hidden = filtered.length !== 0;
    }
    if (this.elements['empty-actions']) this.elements['empty-actions'].hidden = !state.query;
    if (this.elements.browse) {
      this.elements.browse.href = stateURL(Object.assign({}, state, { query: '', category: '', page: 1 })).href;
    }
    writeState(state, push);
    this.renderPagination(totalPages);
    if (focusHeading && this.elements.heading) {
      this.elements.heading.setAttribute('tabindex', '-1');
      this.elements.heading.focus({ preventScroll: true });
      this.elements.heading.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    this.root.dispatchEvent(new CustomEvent('magazine:archive:render', { bubbles: true }));
  };

  Archive.prototype.search = function (query) {
    this.state.query = cleanQuery(query);
    this.state.category = '';
    this.state.label = '';
    this.state.page = 1;
    if (this.records) this.render(true, true);
    else writeState(this.state, true);
  };

  Archive.prototype.syncSearchFilled = function () {
    var input = this.elements['search-input'];
    if (input) input.classList.toggle('is-filled', input.value !== '');
  };

  Archive.prototype.focusSearch = function () {
    var input = this.elements['search-input'];
    if (!this.state.query || !input || !this.elements.search || this.elements.search.hidden) return false;
    var rect = input.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) input.scrollIntoView({ block: 'center' });
    input.focus();
    input.select();
    return true;
  };

  Archive.prototype.clearSearch = function () {
    if (!this.state.query) return false;
    this.state.query = '';
    this.state.page = 1;
    if (this.records) this.render(true, true);
    else writeState(this.state, true);
    return true;
  };

  Archive.prototype.destroy = function () {
    this.destroyed = true;
    this.root.removeEventListener('change', this.onChange);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('submit', this.onSubmit);
    this.root.removeEventListener('input', this.onInput);
    controllers.delete(this.root);
    activeControllers.delete(this);
  };

  function rootsWithin(scope) {
    var roots = Array.from(scope.querySelectorAll('[data-magazine-archive]'));
    if (scope.matches && scope.matches('[data-magazine-archive]')) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    rootsWithin(scope || document).forEach(function (root) {
      if (controllers.has(root)) return;
      var controller = new Archive(root);
      controllers.set(root, controller);
      activeControllers.add(controller);
    });
  }

  function eachController(callback) {
    var handled = false;
    activeControllers.forEach(function (controller) {
      if (!controller.root.isConnected) {
        controller.destroy();
        return;
      }
      if (callback(controller) !== false) handled = true;
    });
    return handled;
  }

  function search(query) {
    return eachController(function (controller) {
      controller.search(query);
    });
  }

  function clearSearch() {
    return eachController(function (controller) {
      return controller.clearSearch();
    });
  }

  function focusSearch() {
    var handled = false;
    activeControllers.forEach(function (controller) {
      if (!handled && controller.root.isConnected) handled = controller.focusSearch();
    });
    return handled;
  }

  window.MagazineArchive = {
    load: load,
    init: init,
    search: search,
    clearSearch: clearSearch,
    focusSearch: focusSearch,
  };
  window.addEventListener('popstate', function () {
    activeControllers.forEach(function (controller) {
      if (!controller.root.isConnected) {
        controller.destroy();
        return;
      }
      controller.state = readState();
      controller.render(false);
    });
  });
  document.addEventListener('shopify:section:unload', function (event) {
    rootsWithin(event.target).forEach(function (root) {
      var controller = controllers.get(root);
      if (controller) controller.destroy();
      cache.delete(sourceURL(root.dataset.sourceUrl).href);
    });
  });
  document.addEventListener('shopify:section:load', function (event) {
    rootsWithin(event.target).forEach(function (root) {
      cache.delete(sourceURL(root.dataset.sourceUrl).href);
    });
    init(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else init(document);
})();
